import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { lieferantenAPI } from '../lib/api';

const EMPTY = {
  name: '',
  ust_id: '',
  iban: '',
  default_konto: '',
  default_ust_satz: '',
  is_eu_business: false,
};

// Strip empty strings so the backend treats them as "not set" rather than ""
function cleanPayload(form, isEdit, original) {
  const out = {};
  for (const k of Object.keys(EMPTY)) {
    const v = form[k];
    if (k === 'is_eu_business') {
      // Always include the boolean
      if (!isEdit || v !== original[k]) out[k] = v;
      continue;
    }
    const norm = v === '' ? null : v;
    if (!isEdit) {
      if (norm !== null && norm !== false) out[k] = norm;
    } else if (norm !== (original[k] ?? null)) {
      out[k] = norm;
    }
  }
  return out;
}

export default function LieferantFormModal({ open, onClose, slug, lieferant, onSaved }) {
  const isEdit = !!lieferant;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (lieferant) {
      setForm({
        name: lieferant.name || '',
        ust_id: lieferant.ust_id || '',
        iban: lieferant.iban || '',
        default_konto: lieferant.default_konto || '',
        default_ust_satz:
          lieferant.default_ust_satz !== null && lieferant.default_ust_satz !== undefined
            ? String(lieferant.default_ust_satz).replace('.00', '')
            : '',
        is_eu_business: !!lieferant.is_eu_business,
      });
    } else {
      setForm(EMPTY);
    }
  }, [lieferant, open]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    setBusy(true);
    try {
      const payload = cleanPayload(form, isEdit, lieferant || {});
      if (isEdit) {
        await lieferantenAPI.patch(slug, lieferant.id, payload);
        toast.success('Aktualisiert');
      } else {
        await lieferantenAPI.create(slug, payload);
        toast.success('Angelegt');
      }
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="modal-overlay">
        <Transition.Child
          as={Fragment}
          enter="fade-enter" enterFrom="fade-enter-from" enterTo="fade-enter-to"
          leave="fade-leave" leaveFrom="fade-leave-from" leaveTo="fade-leave-to"
        >
          <div className="modal-backdrop" aria-hidden="true" />
        </Transition.Child>

        <div className="modal-positioner">
          <Transition.Child
            as={Fragment}
            enter="slide-enter" enterFrom="slide-enter-from" enterTo="slide-enter-to"
            leave="slide-leave" leaveFrom="slide-leave-from" leaveTo="slide-leave-to"
          >
            <Dialog.Panel className="modal-panel">
              <form onSubmit={onSubmit}>
                <div className="modal-header">
                  <Dialog.Title className="modal-title">
                    {isEdit ? `Lieferant bearbeiten: ${lieferant.name}` : 'Neuer Lieferant'}
                  </Dialog.Title>
                  <button type="button" className="btn-text" onClick={onClose} aria-label="Schließen">
                    <X size={16} />
                  </button>
                </div>

                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
                  <label style={{ gridColumn: '1 / span 2' }}>
                    Name *
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                      required
                      autoFocus
                    />
                  </label>

                  <label>
                    USt-IdNr
                    <input
                      type="text"
                      value={form.ust_id}
                      onChange={(e) => setField('ust_id', e.target.value)}
                      placeholder="DE123456789"
                    />
                  </label>

                  <label>
                    IBAN
                    <input
                      type="text"
                      value={form.iban}
                      onChange={(e) => setField('iban', e.target.value)}
                    />
                  </label>

                  <label>
                    Standard-Konto
                    <input
                      type="text"
                      value={form.default_konto}
                      onChange={(e) => setField('default_konto', e.target.value)}
                      placeholder="z.B. 4930"
                      pattern="\d{3,8}"
                      title="3-8 Ziffern"
                    />
                  </label>

                  <label>
                    USt-Satz Standardwert
                    <select
                      value={form.default_ust_satz}
                      onChange={(e) => setField('default_ust_satz', e.target.value)}
                    >
                      <option value="">— keiner —</option>
                      <option value="0">0%</option>
                      <option value="7">7%</option>
                      <option value="19">19%</option>
                    </select>
                  </label>

                  <label
                    style={{
                      gridColumn: '1 / span 2',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.is_eu_business}
                      onChange={(e) => setField('is_eu_business', e.target.checked)}
                      style={{ width: 'auto' }}
                    />
                    EU-Unternehmen (Reverse-Charge anwendbar)
                  </label>
                </div>

                <div className="actions" style={{ borderTop: 'none', paddingTop: 12 }}>
                  <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn-primary" disabled={busy}>
                    {isEdit ? 'Speichern' : 'Anlegen'}
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
